import type {
  TimeManagementInsights,
  CompletionMetrics,
  TopicAnalysis,
  OverrunAnalysis,
} from "./analyticsService";

export interface SummaryRequest {
  timePeriod: string;
  timeManagementInsights: TimeManagementInsights;
  completionMetrics: CompletionMetrics;
  topicAnalysis: TopicAnalysis;
  overrunAnalysis: OverrunAnalysis;
}

export interface SummaryResponse {
  executiveSummary: string;
  keyInsights: string[];
  areasForImprovement: string[];
  positivePatterns: string[];
  nextSteps: string[];
}

export interface GeminiConfig {
  apiKey: string;
  model?: string;
  maxTokens?: number;
  temperature?: number;
}

class GeminiService {
  private defaultPrompt = `You are an expert productivity analyst and time management coach. Based on the following time tracking data, generate a concise, actionable summary that provides insights and recommendations for improvement.

IMPORTANT: When previous period values are shown (e.g., "Previous: 125.0%"), these represent the same metrics from the immediately preceding time period for comparison. Use these to identify trends and improvements:
- For Average Time Deviation: Lower percentages are better (closer to 100% = more accurate estimates)
- For Session Lengths: Compare current vs previous to identify if session patterns are changing
- Focus on trends and whether performance is improving, declining, or staying consistent

Please respond with a JSON object in the following exact format:

{
  "executiveSummary": "2-3 sentences summarizing key highlights and overall performance, including any notable trends compared to previous periods",
  "keyInsights": [
    "First important finding",
    "Second important finding", 
    "Third important finding"
  ],
  "areasForImprovement": [
    "First specific recommendation",
    "Second specific recommendation"
  ],
  "positivePatterns": [
    "First positive pattern",
    "Second positive pattern"
  ],
  "nextSteps": [
    "First concrete action to try",
    "Second concrete action to try"
  ]
}

Focus on being constructive, specific, and actionable. Use the data to provide personalized insights rather than generic advice. When previous period data is available, analyze trends and changes. Ensure the JSON is valid and properly formatted.

Here's the time tracking data:

`;

  /**
   * Generate a prompt for Gemini
   */
  generatePrompt(data: SummaryRequest): string {
    const formattedData = this.formatDataForPrompt(data);
    return this.defaultPrompt + formattedData;
  }

  /**
   * Format analytics data for Gemini consumption
   */
  private formatDataForPrompt(data: SummaryRequest): string {
    const formatDuration = (ms: number): string => {
      if (ms === 0) return "0m";
      const hours = Math.floor(ms / (1000 * 60 * 60));
      const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
      if (hours === 0) return `${minutes}m`;
      return `${hours}h ${minutes}m`;
    };

    const formatPercentage = (value: number): string => value.toFixed(1);

    const optimalSession =
      data.timeManagementInsights.sessionLengthAnalysis.optimalSessionLength;

    return `Time Period: ${data.timePeriod}
Note: Previous period values show metrics from the immediately preceding time period (e.g., if "Today" is selected, "Previous" shows "Yesterday"; if "Week" is selected, "Previous" shows "Last Week")

TIME MANAGEMENT INSIGHTS:
- Average Time Deviation: ${formatPercentage(
      data.timeManagementInsights.averageTimeDeviation
    )}%${
      data.timeManagementInsights.previousAverageTimeDeviation
        ? ` (Previous: ${formatPercentage(
            data.timeManagementInsights.previousAverageTimeDeviation
          )}%)`
        : ""
    }
- Average Session Length: ${formatDuration(
      data.timeManagementInsights.sessionLengthAnalysis.averageSessionLength
    )}${
      data.timeManagementInsights.sessionLengthAnalysis
        .previousAverageSessionLength
        ? ` (Previous: ${formatDuration(
            data.timeManagementInsights.sessionLengthAnalysis
              .previousAverageSessionLength
          )})`
        : ""
    }
- Optimal Session Length: ${
      optimalSession === null
        ? "Insufficient data"
        : formatDuration(optimalSession)
    }${
      data.timeManagementInsights.sessionLengthAnalysis
        .previousOptimalSessionLength
        ? ` (Previous: ${formatDuration(
            data.timeManagementInsights.sessionLengthAnalysis
              .previousOptimalSessionLength
          )})`
        : ""
    }

Most Accurate Topics:
${data.timeManagementInsights.mostAccurateTopics
  .map(
    (topic) =>
      `  - ${topic.topic}: ${formatPercentage(topic.timeDeviation)}% (${
        topic.taskCount
      } tasks)`
  )
  .join("\n")}

Session Length Distribution:
${data.timeManagementInsights.sessionLengthAnalysis.sessionLengthDistribution
  .map(
    (dist) =>
      `  - ${dist.range}: ${dist.count} sessions (${formatPercentage(
        dist.percentage
      )}%)`
  )
  .join("\n")}

COMPLETION METRICS:
- Total Tasks: ${data.completionMetrics.totalTasks}
- Completed Tasks: ${data.completionMetrics.completedTasks}
- Completion Rate: ${formatPercentage(data.completionMetrics.completionRate)}%
- Total Duration: ${formatDuration(data.completionMetrics.totalDuration)}
- Total Estimated: ${formatDuration(data.completionMetrics.totalEstimated)}

Time Deviation Categories:
- Perfect (≤105%): ${
      data.completionMetrics.timeDeviationByCategory.perfect
    } tasks
- Great (105-110%): ${
      data.completionMetrics.timeDeviationByCategory.great
    } tasks
- Good (110-120%): ${data.completionMetrics.timeDeviationByCategory.good} tasks
- Overrun (>120%): ${
      data.completionMetrics.timeDeviationByCategory.overrun
    } tasks

TOPIC ANALYSIS:
${data.topicAnalysis.topics
  .map(
    (topic) =>
      `- ${topic.name}:
    Tasks: ${topic.totalTasks} (${topic.completedTasks} completed)
    Avg Duration: ${formatDuration(topic.averageDuration)}
    Time Deviation: ${formatPercentage(topic.timeDeviation)}%
    Overrun Rate: ${formatPercentage(topic.overrunRate)}%`
  )
  .join("\n")}

Most Overrun Topics:
${data.topicAnalysis.mostOverrunTopics
  .map((topic) => `  - ${topic.topic}: ${formatPercentage(topic.overrunRate)}%`)
  .join("\n")}

Total Duration by Topic:
${data.topicAnalysis.durationByTopic
  .map(
    (item) =>
      `  - ${item.topic}: ${formatDuration(
        item.totalDuration
      )} (${formatPercentage(item.percentage)}%)`
  )
  .join("\n")}

OVERRUN ANALYSIS:
- Total Overruns: ${data.overrunAnalysis.totalOverruns}
- Overrun Rate: ${formatPercentage(data.overrunAnalysis.overrunRate)}%

Most Common Overrun Reasons:
${data.overrunAnalysis.reasons
  .map(
    (reason) =>
      `  - ${reason.reason}: ${reason.count} times (${formatPercentage(
        reason.percentage
      )}%)`
  )
  .join("\n")}

Please provide your analysis based on this data.`;
  }

  /**
   * Parse Gemini response into structured summary
   */
  parseGeminiResponse(response: string): SummaryResponse {
    const isDevelopment =
      import.meta.env.DEV ||
      (typeof window !== "undefined" &&
        window.location.hostname === "localhost");

    if (isDevelopment) {
      console.log("Parsing JSON response:", response);
    }

    try {
      // Extract JSON from markdown code blocks if present
      let jsonContent = response;

      // Check if response is wrapped in markdown code blocks
      const codeBlockMatch = response.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (codeBlockMatch) {
        jsonContent = codeBlockMatch[1].trim();
        if (isDevelopment) {
          console.log("Extracted JSON from code block:", jsonContent);
        }
      }

      // Try to parse as JSON
      const jsonResponse = JSON.parse(jsonContent);

      // Validate the structure
      if (typeof jsonResponse === "object" && jsonResponse !== null) {
        const summary: SummaryResponse = {
          executiveSummary: jsonResponse.executiveSummary || "",
          keyInsights: Array.isArray(jsonResponse.keyInsights)
            ? jsonResponse.keyInsights
            : [],
          areasForImprovement: Array.isArray(jsonResponse.areasForImprovement)
            ? jsonResponse.areasForImprovement
            : [],
          positivePatterns: Array.isArray(jsonResponse.positivePatterns)
            ? jsonResponse.positivePatterns
            : [],
          nextSteps: Array.isArray(jsonResponse.nextSteps)
            ? jsonResponse.nextSteps
            : [],
        };

        if (isDevelopment) {
          console.log("Successfully parsed JSON response:", summary);
        }
        return summary;
      }
    } catch (error) {
      if (isDevelopment) {
        console.log(
          "Failed to parse as JSON, falling back to text parsing:",
          error
        );
      }

      // Fallback to the old text parsing method
      return this.parseTextResponse(response);
    }

    // If we get here, return empty summary
    if (isDevelopment) {
      console.log("Invalid JSON structure, returning empty summary");
    }
    return {
      executiveSummary: "",
      keyInsights: [],
      areasForImprovement: [],
      positivePatterns: [],
      nextSteps: [],
    };
  }

  /**
   * Fallback method to parse text response (old method)
   */
  private parseTextResponse(response: string): SummaryResponse {
    const isDevelopment =
      import.meta.env.DEV ||
      (typeof window !== "undefined" &&
        window.location.hostname === "localhost");

    if (isDevelopment) {
      console.log("Parsing text response:", response);
      console.log("Response length:", response.length);
      console.log("First 200 chars:", response.substring(0, 200));
    }

    const summary: SummaryResponse = {
      executiveSummary: "",
      keyInsights: [],
      areasForImprovement: [],
      positivePatterns: [],
      nextSteps: [],
    };

    // Extract Executive Summary (section 1)
    const execSummaryMatch = response.match(
      /1\.\s*\*\*Executive Summary:\*\*\s*([\s\S]*?)(?=\n\n2\.|$)/s
    );
    if (execSummaryMatch) {
      summary.executiveSummary = execSummaryMatch[1].trim();
      if (isDevelopment) {
        console.log("Found Executive Summary:", summary.executiveSummary);
      }
    } else if (isDevelopment) {
      console.log("No Executive Summary match found");
    }

    // Extract Key Insights (section 2)
    const keyInsightsMatch = response.match(
      /2\.\s*\*\*Key Insights:\*\*\s*([\s\S]*?)(?=\n\n3\.|$)/s
    );
    if (keyInsightsMatch) {
      summary.keyInsights = this.extractBulletPoints(keyInsightsMatch[1]);
      if (isDevelopment) {
        console.log("Found Key Insights:", summary.keyInsights);
      }
    } else if (isDevelopment) {
      console.log("No Key Insights match found");
    }

    // Extract Areas for Improvement (section 3)
    const improvementsMatch = response.match(
      /3\.\s*\*\*Areas for Improvement:\*\*\s*\n*([\s\S]*?)(?=\n\n4\.|$)/s
    );
    if (improvementsMatch) {
      summary.areasForImprovement = this.extractBulletPoints(
        improvementsMatch[1]
      );
      if (isDevelopment) {
        console.log(
          "Found Areas for Improvement:",
          summary.areasForImprovement
        );
      }
    } else if (isDevelopment) {
      console.log("No Areas for Improvement match found");
    }

    // Extract Positive Patterns (section 4)
    const patternsMatch = response.match(
      /4\.\s*\*\*Positive Patterns:\*\*\s*\n*([\s\S]*?)(?=\n\n5\.|$)/s
    );
    if (patternsMatch) {
      summary.positivePatterns = this.extractBulletPoints(patternsMatch[1]);
      if (isDevelopment) {
        console.log("Found Positive Patterns:", summary.positivePatterns);
      }
    } else if (isDevelopment) {
      console.log("No Positive Patterns match found");
    }

    // Extract Next Steps (section 5)
    const nextStepsMatch = response.match(
      /5\.\s*\*\*Next Steps:\*\*\s*\n*([\s\S]*?)(?=\n\n\*\*|$)/s
    );
    if (nextStepsMatch) {
      summary.nextSteps = this.extractBulletPoints(nextStepsMatch[1]);
      if (isDevelopment) {
        console.log("Found Next Steps:", summary.nextSteps);
      }
    } else if (isDevelopment) {
      console.log("No Next Steps match found");
    }

    if (isDevelopment) {
      console.log("Parsed text response:", summary);
    }
    return summary;
  }

  /**
   * Extract bullet points from text
   */
  private extractBulletPoints(text: string): string[] {
    const isDevelopment =
      import.meta.env.DEV ||
      (typeof window !== "undefined" &&
        window.location.hostname === "localhost");

    if (isDevelopment) {
      console.log("Extracting bullet points from:", text);
    }

    const lines = text.split("\n");
    if (isDevelopment) {
      console.log("Split lines:", lines);
    }

    const bulletPoints = lines
      .map((line) => line.trim())
      .filter(
        (line) =>
          line.startsWith("-") || line.startsWith("•") || line.startsWith("*")
      )
      .map((line) => {
        // Remove bullet point marker and any bold formatting
        return line
          .replace(/^[-•*]\s*/, "") // Remove bullet marker
          .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold formatting
          .trim();
      })
      .filter((line) => line.length > 0);

    if (isDevelopment) {
      console.log("Extracted bullet points:", bulletPoints);
    }

    // If no bullet points found with standard markers, try to find indented lines
    if (bulletPoints.length === 0) {
      if (isDevelopment) {
        console.log(
          "No standard bullet points found, trying indented lines..."
        );
      }
      const indentedLines = lines
        .filter((line) => line.trim().length > 0 && line.startsWith("    "))
        .map((line) => {
          return line
            .trim()
            .replace(/^[-•*]\s*/, "") // Remove bullet marker if present
            .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold formatting
            .trim();
        })
        .filter((line) => line.length > 0);

      if (isDevelopment) {
        console.log("Found indented lines:", indentedLines);
      }
      return indentedLines;
    }

    return bulletPoints;
  }

  /**
   * Generate summary using Google AI Studio API (Gemini)
   */
  async generateSummary(
    data: SummaryRequest,
    config: GeminiConfig
  ): Promise<SummaryResponse> {
    if (!config.apiKey) {
      throw new Error("Gemini API key required");
    }

    const prompt = this.generatePrompt(data);

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${
          config.model || "gemini-2.0-flash-exp"
        }:generateContent?key=${config.apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: prompt,
                  },
                ],
              },
            ],
            generationConfig: {
              maxOutputTokens: config.maxTokens || 1000,
              temperature: config.temperature || 0.7,
            },
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Gemini API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log("Gemini API Response:", result);

      const content = result.candidates?.[0]?.content?.parts?.[0]?.text;
      console.log("Extracted content:", content);

      if (!content) {
        console.error("No content found in response. Full response:", result);
        throw new Error("No content received from Gemini");
      }

      const parsedResponse = this.parseGeminiResponse(content);
      console.log("Parsed response:", parsedResponse);

      return parsedResponse;
    } catch (error) {
      console.error("Error generating summary with Gemini:", error);
      throw error;
    }
  }
}

export const geminiService = new GeminiService();
